import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { RECAP_ACCESS_LEVELS, RecapPermission } from 'features/recaps/constants'
import {
  buildAccessListOrganization,
  countShareRecipients,
  enrichAccessListUserGroups,
  enrichAccessListUsers,
  mergeDeletePermissionsIntoAccessList,
  mergePostPermissionsIntoAccessList,
  splitRecapPermissionTuples,
} from 'features/recaps/modals/share_modal/utils'
import { useLazyFetchRecapPermissionsQuery } from 'hooks/notetaker/api'

import { initialAccessListState } from './constants'

// Resolve permission tuples into owner, user, group, and org rows.
const loadEnrichedAccessList = async ({
  dispatch,
  permissions,
  owner,
  currentUser,
  orgUuid,
}) => {
  const { users, userGroups, organizations } = splitRecapPermissionTuples(permissions)
  const { owner: accessListOwner, users: enrichedUsers } = await enrichAccessListUsers(
    dispatch,
    users,
    owner,
    currentUser,
  )
  const enrichedUserGroups = await enrichAccessListUserGroups(userGroups)

  return {
    owner: accessListOwner,
    users: enrichedUsers,
    userGroups: enrichedUserGroups,
    organization: buildAccessListOrganization(orgUuid, organizations),
  }
}

// Recipient rows and recipient count only shown when access level is Restricted.
const applyAccessListState = ({
  enrichedAccessList,
  permissionUsers,
  accessLevel,
  ownerUserUuid,
  setRecipientCount,
  setAccessList,
  isError = false,
}) => {
  const isRestricted = accessLevel === RECAP_ACCESS_LEVELS.RESTRICTED

  setRecipientCount(isRestricted
    ? countShareRecipients(permissionUsers, enrichedAccessList.userGroups, ownerUserUuid)
    : 0)

  setAccessList({
    owner: enrichedAccessList.owner,
    organization: enrichedAccessList.organization,
    users: isRestricted ? enrichedAccessList.users : [],
    userGroups: isRestricted ? enrichedAccessList.userGroups : [],
    isLoading: false,
    isFetched: true,
    isError,
  })
}

export const useAccessList = ({
  isShareModalOpen,
  accessLevel,
  trackedMeetingUuid,
  recapPermission,
  owner,
  currentUser,
  orgUuid,
}) => {
  const dispatch = useDispatch()
  const [accessList, setAccessList] = useState(initialAccessListState)
  const [recipientCount, setRecipientCount] = useState(0)
  const [permissionsResult, setPermissionsResult] = useState(null)
  const [resolvedAccessList, setResolvedAccessList] = useState(null)
  const [refetchNonce, setRefetchNonce] = useState(0)
  const lastPermissionsResultRef = useRef(null)
  const [fetchPermissions] = useLazyFetchRecapPermissionsQuery()
  const ownerUuid = owner?.uuid

  const refetch = useCallback(() => {
    setRefetchNonce((nonce) => nonce + 1)
  }, [])

  const applyPostMutationResult = useCallback((postResponse, recipientsAtSubmit) => {
    setAccessList((current) => {
      const mergedAccessList = mergePostPermissionsIntoAccessList(current, postResponse, recipientsAtSubmit)

      setRecipientCount(countShareRecipients(
        mergedAccessList.users,
        mergedAccessList.userGroups,
        ownerUuid,
      ))

      return mergedAccessList
    })
  }, [ownerUuid])

  const applyDeleteMutationResult = useCallback((deletePayload) => {
    setAccessList((current) => {
      const mergedAccessList = mergeDeletePermissionsIntoAccessList(current, deletePayload)

      setRecipientCount(countShareRecipients(
        mergedAccessList.users,
        mergedAccessList.userGroups,
        ownerUuid,
      ))

      return mergedAccessList
    })
  }, [ownerUuid])

  const updatePermissionsResult = useCallback((nextPermissionsResult) => {
    const lastPermissionsResult = lastPermissionsResultRef.current

    if (
      lastPermissionsResult != null
      && lastPermissionsResult.isError === nextPermissionsResult.isError
      && lastPermissionsResult.permissions === nextPermissionsResult.permissions
    ) {
      return
    }

    lastPermissionsResultRef.current = nextPermissionsResult
    setPermissionsResult(nextPermissionsResult)
  }, [])

  // Start the permissions query when the modal opens and sharing is allowed.
  useEffect(() => {
    if (!isShareModalOpen || recapPermission === RecapPermission.VIEW || !trackedMeetingUuid) {
      return undefined
    }

    let isCancelled = false

    const loadPermissions = async () => {
      setPermissionsResult(null)
      setResolvedAccessList(null)
      // this prevent infinite loop on open
      lastPermissionsResultRef.current = null
      setAccessList((current) => ({
        ...current,
        isLoading: true,
        isFetched: false,
        isError: false,
      }))

      try {
        // Intentionally do not pass preferCacheValue=true. A valid RTK cache hit is fine,
        // but modal open should not explicitly prefer a stale cache entry over a request.
        const fetchedPermissions = await fetchPermissions(trackedMeetingUuid).unwrap()

        if (isCancelled) return

        updatePermissionsResult({
          isError: false,
          permissions: fetchedPermissions,
        })
      } catch {
        if (isCancelled) return

        updatePermissionsResult({
          isError: true,
          permissions: null,
        })
      }
    }

    loadPermissions()

    return () => {
      isCancelled = true
    }
  }, [
    fetchPermissions,
    isShareModalOpen,
    recapPermission,
    refetchNonce,
    trackedMeetingUuid,
    updatePermissionsResult,
  ])

  useEffect(() => {
    if (!isShareModalOpen || !permissionsResult) {
      return undefined
    }

    let isCancelled = false

    const resolveAccessList = async () => {
      try {
        const permissions = permissionsResult.isError ? null : permissionsResult.permissions
        const { users } = splitRecapPermissionTuples(permissions)

        const enrichedAccessList = await loadEnrichedAccessList({
          dispatch,
          permissions,
          owner,
          currentUser,
          orgUuid,
        })

        if (isCancelled) return

        setResolvedAccessList({
          enrichedAccessList,
          permissionUsers: users,
          isError: permissionsResult.isError,
        })
      } catch {
        if (isCancelled) return

        try {
          const fallbackAccessList = await loadEnrichedAccessList({
            dispatch,
            permissions: null,
            owner,
            currentUser,
            orgUuid,
          })

          if (isCancelled) return

          setResolvedAccessList({
            enrichedAccessList: fallbackAccessList,
            permissionUsers: [],
            isError: true,
          })
        } catch {
          if (isCancelled) return

          setAccessList((current) => ({
            ...current,
            isLoading: false,
            isFetched: true,
            isError: true,
          }))
        }
      }
    }

    resolveAccessList()

    return () => {
      isCancelled = true
    }
  }, [
    currentUser,
    dispatch,
    isShareModalOpen,
    orgUuid,
    ownerUuid,
    permissionsResult,
    owner,
  ])

  useEffect(() => {
    if (!isShareModalOpen || !resolvedAccessList) return

    applyAccessListState({
      enrichedAccessList: resolvedAccessList.enrichedAccessList,
      permissionUsers: resolvedAccessList.permissionUsers,
      accessLevel,
      ownerUserUuid: ownerUuid,
      setRecipientCount,
      setAccessList,
      isError: resolvedAccessList.isError,
    })
  }, [accessLevel, isShareModalOpen, ownerUuid, resolvedAccessList])

  useEffect(() => {
    if (!isShareModalOpen) {
      setAccessList(initialAccessListState)
      setRecipientCount(0)
      setPermissionsResult(null)
      setResolvedAccessList(null)
      lastPermissionsResultRef.current = null
    }
  }, [isShareModalOpen])

  // Update recipient count and access list when access level changes.
  useEffect(() => {
    if (!isShareModalOpen || accessLevel == null) return

    if (accessLevel !== RECAP_ACCESS_LEVELS.RESTRICTED) {
      setRecipientCount(0)
      setAccessList((current) => ({
        ...current,
        users: [],
        userGroups: [],
      }))
    }
  }, [accessLevel, isShareModalOpen])

  return {
    accessList,
    recipientCount,
    setRecipientCount,
    refetch,
    applyPostMutationResult,
    applyDeleteMutationResult,
  }
}
